import { useState } from "react";
import { createFileRoute, redirect, useSearch } from "@tanstack/react-router";
import { LoginField } from "@/components/pixel";
import { Button, Callout, Card } from "@/components/ui";
import { authClient } from "@/lib/auth";
import { BASEPATH, safeRedirect } from "@/lib/redirect";
import { meQueryOptions } from "@/lib/session";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    // Already signed in? Don't show a sign-in button — a second OAuth round
    // trip that lands you where you already were reads as a bug.
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (me) {
      throw redirect({ href: BASEPATH + safeRedirect(search.redirect) });
    }
  },
  component: Login,
});

const DOMAIN = "pilani.bits-pilani.ac.in";

function Login() {
  const { redirect: wanted } = useSearch({ from: "/login" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function signIn() {
    setBusy(true);
    setError(null);
    // A full-page navigation to Google, so this promise normally never
    // settles. It settling means the redirect never started.
    const { error } = await authClient.signIn.social({
      provider: "google",
      callbackURL: BASEPATH + safeRedirect(wanted),
      errorCallbackURL: `${BASEPATH}/login`,
    });
    if (error) {
      setError(error.message ?? "Sign-in failed. Try again.");
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center px-5 py-16">
      <LoginField />

      <Card className="card-scrim w-full max-w-[440px]">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <p className="mono text-ink-3">BITS × Postman Innovation Labs</p>
            <h1 className="display text-[clamp(28px,5vw,40px)]">
              Recruitment portal
            </h1>
            <p className="text-[15px] leading-relaxed text-ink-2">
              Sign in with your institute Google account. One profile, then
              apply to as many verticals as you want to.
            </p>
          </div>

          {error ? <Callout tone="error">{error}</Callout> : null}

          <Button onClick={signIn} disabled={busy} className="w-full">
            {busy ? "Redirecting" : "Continue with Google"}
          </Button>

          <p className="text-[13px] leading-relaxed text-ink-3">
            Only <span className="font-[var(--font-mono)]">@{DOMAIN}</span>{" "}
            addresses can sign in. Your BITS ID and admission year are read from
            that address, so the form never asks for them.
          </p>
        </div>
      </Card>
    </main>
  );
}
